import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Plus, Search, Satellite, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import TablePagination from "../components/TablePagination";
import { ReceiptOverlay, AiBadge } from "../components/ReceiptScan";
import { fmtDate, fmtDateTime } from "../lib/format";
import { CARD_IMAGES } from "../lib/cards";

function VerifyBadge({ v }) {
  if (!v) return <Badge variant="outline" className="border-slate-300 text-slate-600">Belum Disemak</Badge>;
  return v.verdict === "sepadan"
    ? <Badge className="bg-[#198754] hover:bg-[#198754]">Sepadan</Badge>
    : <Badge className="bg-[#DC3545] hover:bg-[#DC3545]">Tidak Sepadan</Badge>;
}

export default function Refuels() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canVerify = user?.role === "admin" || user?.role === "operator";
  const [refuels, setRefuels] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [detail, setDetail] = useState(null);
  const [activeField, setActiveField] = useState(null);

  const load = useCallback(() => api.get("/refuels").then((r) => setRefuels(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/refuels/${id}`);
      setActiveField(null);
      setDetail(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const filtered = refuels.filter((r) => {
    const q = search.toLowerCase();
    return !q || `${r.vehicle_reg} ${r.station_name} ${r.driver_name} ${r.fuel_type || ""} ${r.matched_destination || ""}`.toLowerCase().includes(q);
  });
  const paged = filtered.slice((page - 1) * perPage, page * perPage);
  const tv = detail?.telematics_verification;

  return (
    <div className="space-y-6 fade-up" data-testid="refuels-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Isi Minyak</h1>
          <p className="text-sm text-muted-foreground mt-1">Rekod aktiviti isi minyak dengan resit — dipadankan automatik dengan tempahan kenderaan.</p>
        </div>
        <Button className="bg-[#10056E] hover:bg-[#2a17a8]" onClick={() => navigate("/isi-minyak/baharu")} data-testid="new-refuel-btn">
          <Plus className="h-4 w-4 mr-2" /> Rekod Isi Minyak
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-semibold">{filtered.length} rekod</p>
          <div className="relative w-full sm:w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Cari kenderaan, stesen, staf..." value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="refuel-search-input" />
          </div>
        </div>
        {/* Mobile cards */}
        <div className="sm:hidden divide-y" data-testid="refuels-mobile-list">
          {paged.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">Tiada rekod isi minyak.</p>}
          {paged.map((r) => (
            <div key={r.id} className="p-4 active:bg-slate-50" onClick={() => openDetail(r.id)} data-testid={`refuel-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate">{r.vehicle_reg} · {r.liters} L{r.fuel_type ? ` · ${r.fuel_type}` : ""}</p>
                  <p className="text-xs text-muted-foreground truncate">{r.station_name}</p>
                </div>
                {canVerify && <VerifyBadge v={r.telematics_verification} />}
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                {fmtDateTime(r.refuel_datetime)} · RM {Number(r.amount_rm).toFixed(2)} · {r.payment_label}
              </p>
              {r.matched_destination && <p className="text-xs text-[#10056E] mt-0.5">Tempahan: {r.matched_destination}</p>}
            </div>
          ))}
        </div>
        <div className="hidden sm:block">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/70">
              <TableHead>Tarikh & Masa</TableHead>
              <TableHead>Kenderaan</TableHead>
              {canVerify && <TableHead>Staf</TableHead>}
              <TableHead>Stesen</TableHead>
              <TableHead>Jenis</TableHead>
              <TableHead>Liter</TableHead>
              <TableHead>Jumlah (RM)</TableHead>
              <TableHead>Bayaran</TableHead>
              <TableHead>Padanan Tempahan</TableHead>
              {canVerify && <TableHead>Semakan Telematik</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 && (
              <TableRow><TableCell colSpan={canVerify ? 10 : 8} className="text-center text-muted-foreground py-10">Tiada rekod isi minyak.</TableCell></TableRow>
            )}
            {paged.map((r) => (
              <TableRow key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(r.id)} data-testid={`refuel-row-${r.id}`}>
                <TableCell className="text-sm">{fmtDateTime(r.refuel_datetime)}</TableCell>
                <TableCell className="text-sm font-semibold">{r.vehicle_reg}</TableCell>
                {canVerify && <TableCell className="text-sm">{r.driver_name}</TableCell>}
                <TableCell className="text-sm">{r.station_name}</TableCell>
                <TableCell className="text-sm">{r.fuel_type || "-"}{r.used_budi ? <span className="text-[10px] text-[#198754] font-bold ml-1">BUDI</span> : ""}</TableCell>
                <TableCell className="text-sm">{r.liters} L</TableCell>
                <TableCell className="text-sm font-semibold">RM {Number(r.amount_rm).toFixed(2)}</TableCell>
                <TableCell><Badge variant="outline" className={r.payment_category === "kad_minyak" ? "border-[#10056E] text-[#10056E]" : "border-slate-300 text-slate-600"}>{r.payment_label}</Badge></TableCell>
                <TableCell className="text-sm">{r.matched_destination || <span className="text-muted-foreground">-</span>}</TableCell>
                {canVerify && <TableCell><VerifyBadge v={r.telematics_verification} /></TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
        <TablePagination page={page} setPage={setPage} perPage={perPage} setPerPage={setPerPage} total={filtered.length} />
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className={`bg-white max-h-[92vh] overflow-y-auto ${detail?.receipt_image ? "max-w-5xl" : "max-w-xl"}`}>
          <DialogHeader><DialogTitle>Butiran Isi Minyak</DialogTitle></DialogHeader>
          {detail && (
            <div className={`grid gap-5 ${detail.receipt_image ? "md:grid-cols-2" : ""}`} data-testid="refuel-detail">
              {detail.receipt_image && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    Resit Dimuat Naik {detail.ai_fields && <AiBadge />}
                  </p>
                  <ReceiptOverlay image={detail.receipt_image} fields={detail.ai_fields}
                    activeField={activeField} onPickField={(k) => setActiveField(activeField === k ? null : k)} />
                  {detail.ai_fields && (
                    <p className="text-[11px] text-muted-foreground">Kotak berwarna menunjukkan kawasan resit yang dibaca oleh AI — klik untuk fokus.</p>
                  )}
                </div>
              )}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Kenderaan</p><p className="font-bold">{detail.vehicle_reg}</p></div>
                  <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Tarikh & Masa {detail.ai_fields?.date?.value != null && <AiBadge />}</p><p className="font-bold">{fmtDateTime(detail.refuel_datetime)}</p></div>
                  <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Stesen {detail.ai_fields?.station_name?.value != null && <AiBadge />}</p><p className="font-bold">{detail.station_name}</p><p className="text-xs text-muted-foreground">{detail.station_location}</p></div>
                  <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Odometer</p><p className="font-bold">{Number(detail.odometer).toLocaleString()} km</p></div>
                  <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Bahan Api {(detail.ai_fields?.liters?.value != null || detail.ai_fields?.amount_rm?.value != null) && <AiBadge />}</p><p className="font-bold">{detail.fuel_type ? `${detail.fuel_type} · ` : ""}{detail.liters} L</p><p className="text-xs text-muted-foreground">RM {Number(detail.amount_rm).toFixed(2)} (RM {detail.price_per_liter}/L{detail.used_budi ? " · BUDI" : ""})</p></div>
                  <div className="bg-slate-50 border rounded p-3">
                    <p className="text-xs text-muted-foreground">Bayaran {detail.ai_fields?.payment?.value != null && <AiBadge />}</p>
                    {detail.payment_category === "kad_minyak" && CARD_IMAGES[detail.payment_method] && (
                      <img src={CARD_IMAGES[detail.payment_method]} alt={detail.payment_label}
                        className="w-28 rounded-md border my-1.5" data-testid="refuel-detail-card-image" />
                    )}
                    <p className="font-bold">{detail.payment_label}</p>
                    <p className="text-xs text-muted-foreground">{detail.payment_category === "kad_minyak" ? "Kad minyak agensi" : "Wang sendiri — tuntutan"}</p>
                  </div>
                </div>
                <div className={`border rounded-lg p-3 text-sm ${detail.matched_booking_id ? "border-indigo-200 bg-indigo-50" : "border-slate-200 bg-slate-50"}`} data-testid="refuel-match-info">
                  {detail.matched_booking_id ? (
                    <p><b className="text-[#10056E]">Dipadankan:</b> Tempahan ke {detail.matched_destination} · Staf: {detail.matched_driver_name}</p>
                  ) : (
                    <p className="text-muted-foreground">Tiada tempahan sepadan pada waktu isi minyak ini.</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">Direkod oleh {detail.driver_name} · {fmtDate(detail.created_at)}</p>
                </div>
                {detail.station_lat && (
                  <div className="rounded-lg overflow-hidden border" data-testid="refuel-detail-map">
                    <MapContainer center={[detail.station_lat, detail.station_lng]} zoom={15} scrollWheelZoom={false} dragging={false}
                      style={{ height: 160, width: "100%", zIndex: 0 }}>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                      <CircleMarker center={[detail.station_lat, detail.station_lng]} radius={10} pathOptions={{ color: "#10056E", fillColor: "#10056E", fillOpacity: 0.7 }} />
                    </MapContainer>
                  </div>
                )}
                {canVerify && tv && (
                  <div className={`border rounded-lg p-3 ${tv.verdict === "sepadan" ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="refuel-verification">
                    <p className={`text-sm font-semibold flex items-center gap-2 ${tv.verdict === "sepadan" ? "text-emerald-700" : "text-red-700"}`}>
                      <Satellite className="h-4 w-4" /> Semakan Telematik (Auto): {tv.verdict === "sepadan" ? "SEPADAN" : "TIDAK SEPADAN"}
                    </p>
                    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs mt-2 text-slate-700">
                      <p className="flex items-center gap-1.5">{tv.location_match ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-red-600" />} Lokasi GPS: {tv.gps_distance_from_station_km} km dari stesen didakwa</p>
                      <p className="flex items-center gap-1.5">{tv.fuel_match ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-red-600" />} Paras minyak: {tv.fuel_level_before_pct}% → {tv.fuel_level_after_pct}% (+{tv.fuel_increase_pct}%)</p>
                      <p>Jangkaan kenaikan: +{tv.expected_increase_pct}% ({detail.liters} L)</p>
                      <p>Disemak secara automatik semasa rekod dihantar</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
